import { useEffect, useState } from "react";
import { dataApi } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const levelColor = {
  critical: "bg-red-500/10 border-red-500/30 text-red-500",
  high: "bg-amber-500/10 border-amber-500/30 text-amber-500",
  medium: "bg-primary/10 border-primary/30 text-primary",
  low: "bg-muted border-border text-muted-foreground",
};

export default function RiskCenter() {
  const [data, setData] = useState(null);

  useEffect(() => {
    dataApi.risks().then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <PageHeader testid="risks-header" title="Risk Center" subtitle={`${data.risks.length} risks tracked`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard title="By Category" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.by_category}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 10 }} tickFormatter={(v) => v.replace("_", " ")} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(340 82% 58%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Risk Matrix" subtitle="Impact × Likelihood">
          <div className="grid grid-cols-5 gap-1.5">
            {[5, 4, 3, 2, 1].map((impact) =>
              [1, 2, 3, 4, 5].map((likelihood) => {
                const item = data.matrix.find((m) => m.impact === impact && m.likelihood === likelihood);
                const score = impact * likelihood;
                const bg = score >= 15 ? "bg-red-500/60" : score >= 9 ? "bg-amber-500/60" : score >= 4 ? "bg-primary/40" : "bg-muted";
                return (
                  <div key={`${impact}-${likelihood}`} className={`aspect-square rounded ${bg} flex items-center justify-center text-xs font-medium text-white`}>
                    {item?.count || ""}
                  </div>
                );
              })
            )}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground"><span>Low Likelihood</span><span>High</span></div>
        </SectionCard>
      </div>

      <SectionCard title="Open Risks">
        <div className="space-y-3">
          {data.risks.map((r) => (
            <div key={r.id} data-testid={`risk-${r.id}`} className={`rounded-xl border p-4 ${levelColor[r.level]}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.title}</span>
                    <Badge variant="outline" className="capitalize text-[10px]">{r.level}</Badge>
                    <Badge variant="secondary" className="capitalize text-[10px]">{r.category.replace("_", " ")}</Badge>
                  </div>
                  <div className="text-xs mt-1 opacity-80">{r.description}</div>
                  {r.ai_recommendation && (
                    <div className="mt-3 flex items-start gap-2 p-2 rounded-md bg-background/60 border border-border/50">
                      <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                      <div className="text-xs text-foreground">{r.ai_recommendation}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
